import { useCallback, useEffect, useState } from 'react'
import { useGame } from '../../../../contexts/GameContext'
import styles from './GameReport.module.css'
import { Button } from '../../../../common/components/button/Button'
import { createPDFfromHTML } from '../../../reports/components/ReportCommon'
import classNames from 'classnames'
import { t } from 'i18next'
import { getIcon } from '../../../../common/components/icons/utils'
import { PlayerFilters, ReportSortDir, useReportData } from '../../../../contexts/GameReportContextProvider'
import { Badge, GamePeople, Task } from '../../../../types/commonTypes'
import { SelectFormField } from '../../../../common/components/Form/SelectFormField/SelectFormField'
import { Form, FormSpy } from 'react-final-form'
import { PlayersTab } from './PlayersTab'
import { SummaryTab } from './SummaryTab'
import { TasksTab } from './TasksTab'
import { AnswersTab } from './AnswersTab'
import Excel, { Borders } from 'exceljs'
import {
  getPlayerAnswersSheetData,
  getPlayersSheetData,
  getScoreSummarySheetData,
  getTasksSheetData,
} from './ExcelExportHelper'
import saveAs from 'file-saver'
import { parseDateTimeToString } from '../../../../util/string'
import { ReportFiltersForm } from '@/pages/reports/components/ReportFiltersForm'
import { ReportFilters } from '@/contexts/ReportsContextProvider'

type GameReportProps = {
  gameId: number | null
}

export const GameReport: React.FC<GameReportProps> = ({ gameId }) => {
  const [hideShadows, setHideShadows] = useState(false)
  const { gameData, people } = useGame()
  const [activeTab, setActiveTab] = useState(0)
  const [answerFilters, setAnswerFilters] = useState<AnswerFilters>({})
  const { summaryData, setPlayerFilters, playerFilters } = useReportData()
  const [initialOpenFilters, setInitialOpenFilters] = useState<boolean>(false)
  const [tasksSortDir, setTasksSortDir] = useState<ReportSortDir>()
  const [answerSort, setAnswerSort] = useState<AnswerSort>()
  const { tasksData, getAllPlayerDataRows } = useReportData()

  const handleDownloadPDF = useCallback(() => {
    setHideShadows(true)
    setTimeout(() => {
      createPDFfromHTML('PDF_DIV', 'compare')
      setHideShadows(false)
    }, 25)
  }, [])

  const handleDownloadXLS = useCallback(async () => {
    const fileExtension = '.xlsx'
    const noPointsGame = gameData?.advancedSettings.noPointsGame
    const cellBorders: Borders = {
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      top: { style: 'thin' },
      right: { style: 'thin' },
      diagonal: {},
    }

    if (!tasksData) return

    const workbook = new Excel.Workbook()
    const worksheets: Excel.Worksheet[] = []

    // SHEET 1 - GAME SUMMARY
    const { columns, rowData } = getPlayerAnswersSheetData(tasksData, people.players, noPointsGame)
    worksheets.push(workbook.addWorksheet('GAME SUMMARY'))
    worksheets[0].columns = columns
    worksheets[0].columns.forEach((column, index) => {
      if ([0, 2].includes(index)) {
        column.width = 25
        column.alignment = { horizontal: 'left', vertical: 'top' }
      } else if ([5, 7].includes(index)) {
        column.width = 30
        column.alignment = { horizontal: 'left', wrapText: true, vertical: 'top' }
      } else {
        column.width = (column.header?.length ?? 0) + 5
        column.alignment = { horizontal: 'center', vertical: 'top' }
      }
    })
    rowData.forEach((singleData) => {
      worksheets[0].addRow(singleData)
    })

    //SHEET 2 - SCORE SUMMARY
    const sheet2data = getScoreSummarySheetData(tasksData, people.players, noPointsGame)
    worksheets.push(workbook.addWorksheet('SCORE SUMMARY'))
    worksheets[1].columns = sheet2data.columns
    worksheets[1].columns.forEach((column, index) => {
      if (index === 0) {
        column.width = 20
        column.alignment = { horizontal: 'left', vertical: 'top' }
      } else {
        column.width = Math.max(column.header?.length ?? 0, 10)
        column.alignment = { horizontal: 'center', vertical: 'top' }
      }
    })
    worksheets[1].getRow(1).alignment = { horizontal: 'center', wrapText: true, vertical: 'top' }
    sheet2data.rowData.forEach((singleData) => {
      worksheets[1].addRow(singleData)
    })

    //SHEET 3 - PLAYERS SUMMARY
    const sheet3data = getPlayersSheetData(getAllPlayerDataRows!(), gameData?.maxPoints ?? 0, noPointsGame)
    worksheets.push(workbook.addWorksheet('PLAYERS'))
    worksheets[2].columns = sheet3data.columns
    worksheets[2].columns.forEach((column, index) => {
      if (index === 5 || index === 6) {
        column.width = 25
        column.alignment = { horizontal: 'center', vertical: 'top' }
      } else {
        column.width = (column.header?.length ?? 0) + 5
        column.alignment = { horizontal: 'center', vertical: 'top' }
      }
    })
    worksheets[2].getRow(1).alignment = { horizontal: 'center', wrapText: true, vertical: 'top' }
    sheet3data.rowData.forEach((singleData) => {
      worksheets[2].addRow(singleData)
    })

    //SHEET 4 - TASKS SUMMARY
    const sheet4data = getTasksSheetData(tasksData, noPointsGame)
    worksheets.push(workbook.addWorksheet('TASKS'))
    worksheets[3].columns = sheet4data.columns
    worksheets[3].columns.forEach((column, index) => {
      if (index === 0) {
        column.width = 25
        column.alignment = { horizontal: 'left', vertical: 'top' }
      } else if (index === 1) {
        column.width = 20
        column.alignment = { horizontal: 'left', vertical: 'top' }
      } else {
        column.width = (column.header?.length ?? 0) + 5
        column.alignment = { horizontal: 'center', vertical: 'top' }
      }
    })
    sheet4data.rowData.forEach((singleData) => {
      worksheets[3].addRow(singleData)
    })

    //SET COLORS TO ALL SHEETS
    worksheets.forEach((worksheet) => {
      const cellCount = worksheet.getRow(1).cellCount
      worksheet.eachRow({ includeEmpty: false }, (row, index) => {
        for (let ind = 1; ind <= cellCount; ind++) {
          const cell = row.getCell(ind)
          cell.border = cellBorders
          cell.font = { size: 14 }
          if (index % 2 === 1) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: index === 1 ? 'd9e1f2' : 'f2f2f2' } }
          }
        }
      })
      worksheet.getRow(1).font = { bold: true, size: 14 }
      worksheet.views = [{ state: 'frozen', ySplit: 1 }]
    })

    const buf = await workbook.xlsx.writeBuffer()
    saveAs(new Blob([buf]), (gameData?.name ?? 'export') + fileExtension)
  }, [
    gameData?.advancedSettings.noPointsGame,
    gameData?.maxPoints,
    gameData?.name,
    getAllPlayerDataRows,
    people.players,
    tasksData,
  ])

  const handleSetActiveTab = useCallback(
    (tabIndex: number) => {
      setActiveTab(tabIndex)
      if (tabIndex === 1) {
        const temp: PlayerFilters = JSON.parse(JSON.stringify(playerFilters))
        delete temp.badgeId
        delete temp.completion
        setPlayerFilters(temp)
      }
    },
    [setPlayerFilters, playerFilters],
  )

  const handleBadgeClick = useCallback(
    (badgeId: number) => {
      setPlayerFilters({ badgeId })
      setInitialOpenFilters(true)
      setTimeout(() => {
        setInitialOpenFilters(false)
      }, 100)
      setActiveTab(1)
    },
    [setPlayerFilters],
  )

  const handleViewPlayers = useCallback(() => {
    setPlayerFilters({ completion: 'uncompleted' })
    setActiveTab(1)
    setInitialOpenFilters(true)
    setTimeout(() => {
      setInitialOpenFilters(false)
    }, 100)
    setActiveTab(1)
  }, [setPlayerFilters])

  const handleJumpToTasks = useCallback((sortDir: ReportSortDir) => {
    setTasksSortDir(sortDir)
    setActiveTab(2)
    setTimeout(() => {
      setTasksSortDir(undefined)
    }, 100)
  }, [])

  const [internalStartDate, setInternalStartDate] = useState<Date>()
  const [internalEndDate, setInternalEndDate] = useState<Date>()

  useEffect(() => {
    if (playerFilters.startDate !== internalStartDate){
      setPlayerFilters({ ...playerFilters, startDate: internalStartDate })
    } 
    if (playerFilters.endDate !== internalEndDate)
      setPlayerFilters({ ...playerFilters, endDate: internalEndDate })
  }, [internalEndDate, internalStartDate, playerFilters, setPlayerFilters])

  const setDateFiltersInternal = useCallback(
    (filters: ReportFilters) => {
      setInternalStartDate(filters.createdAtFrom ? new Date(filters.createdAtFrom) : undefined)
      setInternalEndDate(filters.createdAtUntil ? new Date(filters.createdAtUntil) : undefined)
    },
    [],
  )

  return (
    <div className={classNames(styles.container, hideShadows && styles.hideShadows)} id={'PDF_DIV'}>
      <div className={styles.headerRow}>
        <h1>{t('game_summary.game_summary_title', 'Game Summary')}</h1>
        <Button style={{ marginLeft: 'auto' }} variant='outline-normal' onClick={handleDownloadPDF}>
          {t('reports.download_pdf', 'Export as PDF')}
        </Button>
        <Button variant='outline-normal' onClick={handleDownloadXLS}>
          {t('reports.download_excel', 'Export Excel')}
        </Button>
      </div>
      <div className={styles.gameBasicsContainer}>
        <div className={styles.gameBasicsLeft}>
          <div className={styles.gameTitle}>{summaryData?.gameName}</div>
          <div className={styles.gameBasics}>
            {t('game_summary.game_creator', 'Creator') + ': ' + gameData?.creator}
          </div>
          <div className={styles.gameBasics}>
            {t('game_summary.game_status', 'Status') +
              ': ' +
              (gameData?.open ? t('game_summary.game_active', 'Active') : t('game_summary.game_closed', 'Not active'))}
          </div>
          <div className={styles.gameBasics}>
            {t('game_summary.game_created', 'Created') +
              ': ' +
              (gameData?.createdAt ? parseDateTimeToString(gameData.createdAt) : '00:00:00')}
          </div>
          <div className={styles.gameBasics}>
            {t('game_summary.game_updated', 'Last updated') +
              ': ' +
              (gameData?.updatedAt ? parseDateTimeToString(gameData.updatedAt) : '00:00:00')}
          </div>
        </div>
        <div>
          <img className={styles.boardImage} src={gameData?.gameBoardSettings.gameBoards[0].url} alt='' />
        </div>
      </div>
      <div className={styles.gameBasics}>
        <ReportFiltersForm onChange={setDateFiltersInternal} customTitle={t('game_summary.review_dates_title', 'Limit statistics by player\'s starting date')} />
      </div>
      <SummaryTabs
        selectedTab={activeTab}
        onSelectTab={handleSetActiveTab}
        badges={gameData?.badges}
        tasks={gameData?.tasks}
        people={people}
        onChangePlayerFilters={activeTab === 1 ? setPlayerFilters : undefined}
        onChangeAnswerFilters={activeTab === 3 ? setAnswerFilters : undefined}
        onChangeAnswerSort={setAnswerSort}
        playerFilters={playerFilters}
        showFiltersInitially={initialOpenFilters}
      />
      {activeTab === 0 && (
        <SummaryTab
          gameData={gameData}
          summaryData={summaryData}
          onBadgeClick={handleBadgeClick}
          onViewPlayers={handleViewPlayers}
          onJumpToTasks={handleJumpToTasks}
        />
      )}
      {activeTab === 1 && (
        <PlayersTab
          maxPoints={gameData?.maxPoints}
          customCompletionEnabled={gameData?.advancedSettings.hasCustomCompletion}
          noPointsGame={gameData?.advancedSettings.noPointsGame}
        />
      )}
      {activeTab === 2 && (
        <TasksTab
          maxPoints={gameData?.maxPoints}
          noPointsGame={gameData?.advancedSettings.noPointsGame}
          externalTasksSortDir={tasksSortDir}
        />
      )}
      {activeTab === 3 && <AnswersTab answerFilters={answerFilters} answerSort={answerSort} playerFilters={playerFilters} />}
    </div>
  )
}

export type AnswerFilters = { taskId?: number; playerId?: number }
export type AnswerSort = { sortOrder: 'lastActionFirst' | 'original' }

type SummaryTabsProps = {
  selectedTab: number
  onSelectTab: (tabIndex: number) => void
  onChangePlayerFilters?: (filters: Partial<PlayerFilters>) => void
  onChangeAnswerFilters?: (filters: AnswerFilters) => void
  onChangeAnswerSort?: (sort: AnswerSort) => void
  badges?: Badge[]
  tasks?: Task[]
  people?: GamePeople
  playerFilters?: PlayerFilters
  showFiltersInitially?: boolean
}

export const SummaryTabs: React.FC<SummaryTabsProps> = ({
  selectedTab,
  onSelectTab,
  onChangePlayerFilters,
  onChangeAnswerFilters,
  onChangeAnswerSort,
  badges,
  tasks,
  people,
  playerFilters,
  showFiltersInitially,
}) => {
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    if (showFiltersInitially) {
      setShowFilters(true)
    }
  }, [showFiltersInitially])

  const handleToggleFilters = useCallback(() => {
    setShowFilters((prev) => !prev)
  }, [])

  const getCompletionOptions = useCallback(() => {
    return [
      { value: 'completed', label: t('game_summary.game_completed', 'Game completed') },
      { value: 'uncompleted', label: t('game_summary.not_completed', 'Not completed') },
    ]
  }, [])

  const getAnswerSortOptions = useCallback(() => {
    return [
      { value: 'original', label: t('game_summary.original_game_order', 'Original Game Order') },
      { value: 'lastActionFirst', label: t('game_summary.latest_action_first', 'Latest action first') },
    ]
  }, [])

  const getBadgeOptions = useCallback(() => {
    return (
      badges &&
      badges.map((badge) => {
        return { value: badge.id, label: badge.name }
      })
    )
  }, [badges])

  const getTaskOptions = useCallback(() => {
    return (
      tasks &&
      tasks?.map((task) => {
        return { value: task.id, label: task.name }
      })
    )
  }, [tasks])

  const getPlayerOptions = useCallback(() => {
    return (
      people?.players &&
      people.players
        ?.filter((p) => !p.deleted)
        .map((player) => {
          return { value: player.id, label: player.nickName }
        })
    )
  }, [people?.players])

  const handleFiltersInternal = useCallback(
    (props: any) => {
      if (onChangePlayerFilters && !showFiltersInitially) onChangePlayerFilters(props.values)
      if (onChangeAnswerFilters) onChangeAnswerFilters(props.values)
    },
    [onChangeAnswerFilters, onChangePlayerFilters, showFiltersInitially],
  )

  const handleAnswerSortInternal = useCallback(
    (props: any) => {
      if (onChangeAnswerSort) onChangeAnswerSort(props.values)
    },
    [onChangeAnswerSort],
  )

  return (
    <div>
      <div className={styles.buttonsContainer}>
        <button
          className={classNames(styles.tabButton, selectedTab === 0 && styles.tabButtonActive)}
          onClick={() => onSelectTab(0)}
        >
          {t('game_summary.tab_summary', 'Summary')}
        </button>

        <button
          className={classNames(styles.tabButton, selectedTab === 1 && styles.tabButtonActive)}
          onClick={() => onSelectTab(1)}
        >
          {t('game_summary.tab_players', 'Players')}
        </button>

        <button
          className={classNames(styles.tabButton, selectedTab === 2 && styles.tabButtonActive)}
          onClick={() => onSelectTab(2)}
        >
          {t('game_summary.tab_tasks', 'Tasks')}
        </button>

        <button
          className={classNames(styles.tabButton, selectedTab === 3 && styles.tabButtonActive)}
          onClick={() => onSelectTab(3)}
        >
          {t('game_summary.tab_answers', 'Answers')}
        </button>
        {(selectedTab === 1 || selectedTab === 3) && (
          <div className={styles.filterArea}>
            {selectedTab === 3 && (
              <Form<AnswerSort> onSubmit={handleAnswerSortInternal}>
                {({ initialValues, values, handleSubmit }) => (
                  <form onSubmit={handleSubmit} className={styles.answerSorter}>
                    <SelectFormField<string>
                      name='sortOrder'
                      options={getAnswerSortOptions()}
                      label={t('game_summary.original_game_order', 'Original Game Order')}
                      placeholder={t('game_summary.answers.game_order_placeholder', 'Sort tasks')}
                      className={styles.answerSortDropdown}
                      srOnlyLabel
                      isClearable
                    />

                    <FormSpy onChange={handleAnswerSortInternal} />
                  </form>
                )}
              </Form>
            )}
            <Button style={{ marginLeft: 'auto' }} variant='outline-normal' onClick={handleToggleFilters}>
              <span className={classNames(styles.filtersIconContainer, showFilters && styles.filtersOpen)}>
                {getIcon('filter')}
              </span>
              {t('game_summary.filters_button', 'Filters')}
            </Button>
          </div>
        )}
      </div>
      {showFilters && onChangePlayerFilters && (
        <div>
          <Form<PlayerFilters> initialValues={playerFilters} onSubmit={onChangePlayerFilters}>
            {({ initialValues, values, handleSubmit }) => (
              <form onSubmit={handleSubmit} className={styles.filtersContainer}>
                <SelectFormField<string>
                  name='completion'
                  options={getCompletionOptions()}
                  label={t('game_summary.filter_label.completion', 'Completion')}
                  placeholder={t('game_summary.filter_label.completion', 'Completion')}
                  srOnlyLabel
                  isClearable
                />
                {badges && (
                  <SelectFormField
                    name='badgeId'
                    options={getBadgeOptions() ?? []}
                    label={t('game_summary.filter_label.by_badge', 'By Badge')}
                    placeholder={t('game_summary.filter_label.by_badge', 'By Badge')}
                    srOnlyLabel
                    isClearable
                  />
                )}

                <FormSpy onChange={handleFiltersInternal} />
              </form>
            )}
          </Form>
        </div>
      )}
      {showFilters && onChangeAnswerFilters && (
        <div>
          <Form<AnswerFilters> onSubmit={onChangeAnswerFilters}>
            {({ values, handleSubmit }) => (
              <form onSubmit={handleSubmit} className={styles.filtersContainer}>
                <SelectFormField<number>
                  name='taskId'
                  options={getTaskOptions() ?? []}
                  label={t('game_summary.filter_label.task', 'Task')}
                  placeholder={t('game_summary.filter_label.task', 'Task')}
                  srOnlyLabel
                  isClearable
                />
                {badges && (
                  <SelectFormField
                    name='playerId'
                    options={getPlayerOptions() ?? []}
                    label={t('game_summary.filter_label.player', 'Player/Team')}
                    placeholder={t('game_summary.filter_label.player', 'Player/Team')}
                    srOnlyLabel
                    isClearable
                    isSearchable
                  />
                )}

                <FormSpy onChange={handleFiltersInternal} />
              </form>
            )}
          </Form>
        </div>
      )}
    </div>
  )
}
